// Coerce the various timestamp shapes we see across web + native Firestore
// SDKs and user-doc fields into a plain JS `Date`.
//
// Accepts:
//   - Firestore `Timestamp` (both web modular and RN Firebase flavors — they
//     expose `.toDate()`)
//   - a bare `{ seconds, nanoseconds }` shape (what Firestore snapshots hand
//     back through some Metro configurations, and what tests construct)
//   - a JS `Date`
//   - an ISO / RFC 2822 date string
//   - null / undefined
//
// Returns `null` for null/undefined input, and `null` for strings that don't
// parse to a valid date (rather than throwing or returning an Invalid Date).

export type TimestampLike =
  | { seconds: number; nanoseconds?: number }
  | { toDate: () => Date }
  | Date
  | string
  | null
  | undefined;

export const toDate = (v: TimestampLike): Date | null => {
  if (v == null) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof (v as { toDate?: () => Date }).toDate === "function") {
    const d = (v as { toDate: () => Date }).toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  const ts = v as { seconds?: number; nanoseconds?: number };
  if (typeof ts.seconds === "number") {
    return new Date(ts.seconds * 1000 + (ts.nanoseconds ?? 0) / 1000000);
  }
  return null;
};
